import type { ArdoqId } from '@ardoq/api-types';
import type { SelectOption } from '@ardoq/select';
import { APIFieldType, UpdatedResponse } from '@ardoq/api-types';
import {
  BooleanDecorator,
  DateDecorator,
  DecoratorWrapper,
} from '@ardoq/decorators';
import { Icon, IconName } from '@ardoq/icons';
import { TruncatedTextPopover } from '@ardoq/popovers';
import {
  UNSET_VALUE_LABEL,
  NoValue,
  MarkdownInPopover,
} from '@ardoq/renderers';
import { Tag, StatusType } from '@ardoq/status-ui';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import styled from 'styled-components';
import { t, type I18nLocale } from '@ardoq/i18n';

const StyledTag = styled(Tag)`
  display: block;
  max-width: fit-content;
`;

type FieldRendererArgs = {
  name: string;
  type: APIFieldType;
  value: any;
  userOptions: SelectOption<ArdoqId>[];
  i18nLocale: I18nLocale;
};

const getFieldRenderer = ({
  name,
  type,
  value,
  userOptions,
  i18nLocale,
}: FieldRendererArgs) => {
  if (name === 'description' || type === APIFieldType.TEXT_AREA) {
    return !value ? (
      <NoValue />
    ) : (
      <MarkdownInPopover value={value} i18nLocale={i18nLocale} />
    );
  }
  switch (type) {
    case APIFieldType.SELECT_MULTIPLE_LIST:
      return value?.length ? (
        <TruncatedTextPopover>{value.join(', ')}</TruncatedTextPopover>
      ) : (
        <NoValue />
      );
    case APIFieldType.CHECKBOX:
      return (
        <BooleanDecorator
          checked={value}
          trueValueLabel={t(
            i18nLocale,
            'surveyAdmin:changeRowTable.checked',
            'Checked'
          )}
          falseValueLabel={t(
            i18nLocale,
            'surveyAdmin:changeRowTable.unchecked',
            'Unchecked'
          )}
          unsetValueLabel={UNSET_VALUE_LABEL}
        />
      );
    case APIFieldType.USER: {
      if (!value) return <NoValue />;
      const userLabel =
        userOptions.find(option => option.value === value)?.label ??
        t(i18nLocale, 'surveyAdmin:changeRowTable.unknownUser', 'Unknown user');
      return <TruncatedTextPopover>{userLabel}</TruncatedTextPopover>;
    }
    case APIFieldType.DATE_ONLY:
    case APIFieldType.DATE_TIME:
    case APIFieldType.DATE_ONLY_RANGE:
    case APIFieldType.DATE_TIME_RANGE:
      return value ? <DateDecorator date={value} /> : <NoValue />;
    default: {
      if (value === undefined || value === null || typeof value === 'object')
        return <NoValue />;
      if (typeof value === 'string')
        return value.length > 0 ? (
          <TruncatedTextPopover>{value}</TruncatedTextPopover>
        ) : (
          <NoValue />
        );
      return <DecoratorWrapper>{value}</DecoratorWrapper>;
    }
  }
};

const ChangeRowTable = ({
  surveyResponse,
  userOptions,
  i18nLocale,
}: {
  surveyResponse: UpdatedResponse;
  userOptions: SelectOption<ArdoqId>[];
  i18nLocale: I18nLocale;
}) => (
  <FlexBox gap="medium" padding="medium">
    <FlexBox flexDirection="column" gap="small" flex={1} minWidth="0">
      <Text variant="captionBold">
        {t(i18nLocale, 'surveyAdmin:changeRowTable.before', 'Before')}
      </Text>
      <StyledTag statusType={StatusType.ERROR}>
        {getFieldRenderer({
          name: surveyResponse.name,
          type: surveyResponse.type,
          value: surveyResponse.previousVal,
          userOptions,
          i18nLocale,
        })}
      </StyledTag>
    </FlexBox>
    <FlexBox flexDirection="column" justify="flex-end" paddingBottom="xsmall">
      <Icon iconName={IconName.ARROW_FORWARD} />
    </FlexBox>
    <FlexBox flexDirection="column" gap="small" flex={1} minWidth="0">
      <Text variant="captionBold">
        {t(i18nLocale, 'surveyAdmin:changeRowTable.now', 'Now')}
      </Text>
      <StyledTag statusType={StatusType.SUCCESS}>
        {getFieldRenderer({
          name: surveyResponse.name,
          type: surveyResponse.type,
          value: surveyResponse.currentVal,
          userOptions,
          i18nLocale,
        })}
      </StyledTag>
    </FlexBox>
  </FlexBox>
);

export { ChangeRowTable };
